import { useEffect, useState } from "react";
import API from "../../../../../api";
import { Box, Typography, Skeleton, Stack } from "@mui/material";
import { LineChart } from "@mui/x-charts";
import dayjs from "dayjs";
import { boxStyle } from "../../../../../theme";

const overlapPercentage = (a, b, c) => {
  let minSum = 0, maxSum = 0;
  for (let i = 0; i < a.length; i++) {
    minSum += Math.min(a[i], b[i], c[i]);
    maxSum += Math.max(a[i], b[i], c[i]);
  }
  return maxSum === 0 ? 100 : (minSum / maxSum) * 100;
};

const similarityColor = (pct) => {
  if (pct < 40) return "#ff4d4d";
  if (pct < 60) return "#ffc107";
  return "#49BECE";
};

const SERIES_CFG = [
  { id: "one",   key: "oneDayAgo",    color: "#ED6B3A", offset: 1 },
  { id: "two",   key: "twoDaysAgo",   color: "#51C3D1", offset: 2 },
  { id: "three", key: "threeDaysAgo", color: "#6451d1", offset: 3 },
];

const TrendGraphCard = () => {
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);

    API.getTiresia({ trendGraph: true }, controller.signal)
      .then((res) => {
        const d = res.data;
        setChartData({
          xData:       d.ore          ?? [],
          oneDayAgo:   d.oneDayAgo    ?? [],
          twoDaysAgo:  d.twoDaysAgo   ?? [],
          threeDaysAgo: d.threeDaysAgo ?? [],
          percentage:  overlapPercentage(
            d.oneDayAgo    ?? [],
            d.twoDaysAgo   ?? [],
            d.threeDaysAgo ?? [],
          ),
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, []);

  return (
    <Box sx={boxStyle}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Typography sx={{ fontWeight: 600, fontFamily: "poppins", fontSize: 20 }}>
          Daily detection trend
        </Typography>

        {loading || !chartData ? (
          <Skeleton variant="text" width={220} height={32} sx={{ bgcolor: "rgba(255,255,255,0.07)" }} />
        ) : (
          <Typography sx={{ fontWeight: 600, fontFamily: "poppins", fontSize: 20 }}>
            Anomaly Similarity:{" "}
            <span style={{ color: similarityColor(chartData.percentage) }}>
              {chartData.percentage.toFixed(2)}
            </span>%
          </Typography>
        )}
      </Box>

      {loading || !chartData ? (
        <Box sx={{ position: "relative", height: 400, pt: 1 }}>
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="flex-end"
            sx={{ height: "100%", px: 1, pb: 3 }}
          >
            {[30, 55, 40, 70, 45, 80, 35, 65, 50, 75, 42, 60,
              28, 52, 38, 68, 48, 78, 33, 62, 47, 72, 40, 58].map((h, i) => (
              <Skeleton
                key={i}
                variant="rounded"
                sx={{
                  flex: 1,
                  height: `${h}%`,
                  bgcolor: "rgba(255,255,255,0.06)",
                  borderRadius: "3px 3px 0 0",
                }}
              />
            ))}
          </Stack>
        </Box>
      ) : (
        <LineChart
          height={400}
          xAxis={[{ data: chartData.xData, label: "Hour", scaleType: "point" }]}
          series={SERIES_CFG.map(({ id, key, color, offset }) => ({
            id,
            data: chartData[key],
            label: dayjs().subtract(offset, "day").format("YYYY-MM-DD"),
            showMark: true,
            valueFormatter: (v) => `${v}`,
            area: true,
            color,
          }))}
          slotProps={{ area: { fillOpacity: 0.1 } }}
        />
      )}
    </Box>
  );
};

export default TrendGraphCard;
